import React from 'react';
import { Grid, TextField, Button, Box, Typography, Autocomplete, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { unableConsentReasons, doctorGroups } from '../constants/options';

const SignatureField = ({ label, name, value, nameValue, dateValue, onSign, onNameChange, signName, isDoctor = false, onChange }) => (
  <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 2, height: '100%' }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#008080' }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {isDoctor ? (
        <FormControl fullWidth size="small">
          <InputLabel>{`Name (${label})`}</InputLabel>
          <Select
            name={signName}
            value={nameValue || ''}
            onChange={onChange}
            label={`Name (${label})`}
          >
            <MenuItem value=""><em>Select Doctor</em></MenuItem>
            {doctorGroups["AnaesthesiaSpecialists"]?.map((doctor, idx) => (
              <MenuItem key={idx} value={doctor}>{doctor}</MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        onNameChange && (
          <TextField
            label="Full Name"
            name={signName}
            value={nameValue || ''}
            onChange={onNameChange}
            size="small"
            fullWidth
          />
        )
      )}
      <Button
        variant="outlined"
        onClick={() => onSign(name)}
        sx={{ color: '#008080', borderColor: '#008080' }}
      >
        {value ? 'Update Signature' : 'Provide Signature'}
      </Button>
      {value && (
        <Box sx={{ border: '1px solid #ccc', p: 1, borderRadius: 1, textAlign: 'center' }}>
          <img
            src={`data:image/png;base64,${value}`}
            alt={`${label} Signature`}
            style={{ maxHeight: '60px', maxWidth: '100%' }}
          />
        </Box>
      )}
      <TextField
        label="Date & Time"
        type="datetime-local"
        value={dateValue || ''}
        InputLabelProps={{ shrink: true }}
        size="small"
        fullWidth
        InputProps={{ readOnly: true }}
      />
    </Box>
  </Box>
);

const RepresentativeConsentSection = ({ formData, handleChange, handleNameChange, handleSignatureOpen, setFormData }) => {
  return (
    <Box sx={{ mt: 6, pt: 4, borderTop: '3px solid #008080' }}>
      <Typography variant="h6" sx={{ color: '#008080', mb: 1, fontWeight: 'bold' }}>
        Step 2: If the Patient is Unable to give Consent
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Fill this section only if the patient is unable to sign for themselves (Children below 18 yrs / vulnerable patients / surrogate).
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            freeSolo
            options={unableConsentReasons}
            value={formData.unable_consent_reason || ''}
            onChange={(e, val) => setFormData(prev => ({ ...prev, unable_consent_reason: val || '' }))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Reason Unable to Consent"
                name="unable_consent_reason"
                onChange={handleChange}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Attender Name"
            name="attender_name"
            value={formData.attender_name || ''}
            onChange={handleNameChange}
            fullWidth
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary">
            (name/relationship to the patient) therefore consent for the procedure with the doctor's designee and hereby consent to this procedure.
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <SignatureField
            label="Patient Representative (with relationship)"
            name="patient_rep_signature"
            value={formData.patient_rep_signature}
            nameValue={formData.patient_rep_name}
            dateValue={formData.patient_rep_signature_date_time}
            onSign={handleSignatureOpen}
            onNameChange={handleNameChange}
            signName="patient_rep_name"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SignatureField
            label="Witness"
            name="patient_rep_witness_signature"
            value={formData.patient_rep_witness_signature}
            nameValue={formData.patient_rep_witness_name}
            dateValue={formData.patient_rep_witness_date_time}
            onSign={handleSignatureOpen}
            onNameChange={handleNameChange}
            signName="patient_rep_witness_name"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SignatureField
            label="Doctor/Anaesthetist"
            name="patient_rep_doctor_anaesthetist_sign"
            value={formData.patient_rep_doctor_anaesthetist_sign}
            nameValue={formData.patient_rep_doctor_anaesthetist_name}
            dateValue={formData.patient_rep_doctor_anaesthetist_sign_date_time}
            onSign={handleSignatureOpen}
            signName="patient_rep_doctor_anaesthetist_name"
            isDoctor={true}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SignatureField
            label="Interpreter (if applicable)"
            name="patient_rep_interpreter_signature"
            value={formData.patient_rep_interpreter_signature}
            nameValue={formData.patient_rep_interpreter_name}
            dateValue={formData.patient_rep_interpreter_signature_date_time}
            onSign={handleSignatureOpen}
            onNameChange={handleNameChange}
            signName="patient_rep_interpreter_name"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default RepresentativeConsentSection;
